import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogIn, Mail } from "lucide-react";
import { OFFICIAL_SEAL, INSTITUTE_NAME } from "@/lib/brand";

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const quickLinks = [
    { href: "/",         label: "Home" },
    { href: "/wait-theory", label: "WAIT Theory" },
    { href: "/scfr-journal", label: "SCFR-Journal" },
    { href: "/research", label: "Research" },
    { href: "/programs", label: "Programs" },
    { href: "/about",    label: "About Us" },
    { href: "/founder",  label: "Founder" },
    { href: "/press",    label: "Press" },
    { href: "/media-kit",label: "Media Kit" },
    { href: "/contact",  label: "Contact" },
  ];

  return (
    <>
      {/* Institutional Executive Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-xl border-b border-[#E5E7EB] shadow-[0_2px_20px_rgba(15,30,71,0.06)]"
            : "bg-white/85 backdrop-blur-md border-b border-[#E5E7EB]/70"
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">

            {/* LEFT: Official Seal + Wordmark */}
            <Link
              to="/"
              className="flex items-center gap-4 group"
              data-testid="header-logo"
            >
              <img
                src={OFFICIAL_SEAL}
                alt={INSTITUTE_NAME}
                className="h-14 lg:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105 drop-shadow-[0_4px_10px_rgba(15,30,71,0.18)]"
                data-testid="official-seal"
              />
              <div className="hidden sm:flex flex-col leading-tight">
                <span
                  className="zodiac-heading text-[#0F1E47] font-bold text-xl lg:text-2xl tracking-tight"
                  data-testid="header-wordmark"
                >ZODIAC</span>
                <span
                  className="hidden md:inline text-[#991B1B] text-[11px] lg:text-[12px] uppercase tracking-[0.22em] font-semibold"
                  data-testid="header-institution"
                >
                  Workforce Intelligence Institute<sup className="text-[8px] lg:text-[9px] ml-0.5 align-super">™</sup>
                </span>
              </div>
            </Link>

            {/* MIDDLE: Inline desktop nav */}
            <nav className="hidden lg:flex items-center gap-7">
              {quickLinks.slice(1, 5).map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === link.href
                      ? "text-[#991B1B]"
                      : "text-[#0F1E47] hover:text-[#1E3A8A]"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g,'-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* RIGHT: Actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Link
                to="/contact"
                className="hidden md:flex items-center gap-2 px-3 py-2 text-[#475569] hover:text-[#0F1E47] text-sm font-medium transition-colors"
                data-testid="header-contact"
              >
                <Mail className="w-4 h-4" />
                Contact
              </Link>
              <Link
                to="/login"
                className="flex items-center gap-2 px-4 py-2 bg-[#0F1E47] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-md transition-colors shadow-[0_4px_14px_rgba(15,30,71,0.18)]"
                data-testid="header-login"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">Portal</span>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-[#0F1E47] hover:bg-[#F4F6FB] rounded-md transition-colors"
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Page spacer to compensate for fixed header */}
      <div className="h-20 lg:h-24" aria-hidden />

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-all duration-300 ${
          mobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-[#0F1E47]/40 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        />
        <div
          className={`absolute top-20 right-4 left-4 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_24px_60px_rgba(15,30,71,0.2)] transform transition-all duration-300 ${
            mobileMenuOpen ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"
          }`}
          data-testid="mobile-menu"
        >
          <div className="p-4 space-y-1">
            {quickLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                  location.pathname === link.href
                    ? "bg-[#FBFAF7] text-[#991B1B]"
                    : "text-[#0F1E47] hover:bg-[#F4F6FB]"
                }`}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g,'-')}`}
              >
                {link.label}
              </Link>
            ))}
            <hr className="border-[#E5E7EB] my-2" />
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-3 rounded-lg bg-[#0F1E47] text-white font-semibold"
              data-testid="mobile-enter-portal"
            >
              <LogIn className="w-5 h-5" />
              Enter Portal
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
